/**
 * Iter 39 / Slice 1 — Technology Sector Tracker dashboard.
 *
 * Sibling component to the Industry Sector dashboard. Reads from the
 * new `/api/tracker-tech/*` namespace; never calls `/api/tracker/*`
 * so the existing Industry path is provably untouched.
 *
 * UI shape (per brief):
 *   Technology Sector ▼   →   Product ▼   →   Vector ▼
 *   [Run discovery]
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ Priority │ Role │ Signal Type │ Why Now │ Evidence │ Angle  │
 *   ├─────────────────────────────────────────────────────────────┤
 *   │ ...      │ ...  │ ...         │ ...     │ ...      │ ...    │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * Slice 1 ships the Atech VMware Displacement vector end-to-end. Every
 * other product (Dynamics 365, ServiceNow, SAP) renders as
 * "No vectors configured yet" so the operator can see the M:M model
 * without believing it discovers anything.
 */
import React, { useEffect, useState } from 'react';
import WideTableScroll from './WideTableScroll';
import { messageFor } from '../lib/apiErrors';
import {
  signalLevelChip,
  rolePillStyle,
  priorityBucket,
  resultTypeChip,
  rowStyleForQualification,
  organisationCell,
  confidenceChip,
  organisationSourceLabel,
  summariseTargets,
  summariseVerdicts,
  verdictChip,
  rowStyleForVerdict,
} from '../lib/technologyVectorDisplay';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TrackerTechnologyDashboard() {
  const [sectors, setSectors] = useState([]);
  const [products, setProducts] = useState([]);
  const [vectors, setVectors] = useState([]);
  // Iter 40 — track in-flight vector fetch so the "No vectors yet"
  // affordance doesn't flash between (a) product selected and (b)
  // vectors fetched.
  const [vectorsLoading, setVectorsLoading] = useState(false);
  const [sector, setSector] = useState('');
  const [product, setProduct] = useState('');
  const [vector, setVector] = useState('');
  const [targets, setTargets] = useState([]);
  const [running, setRunning] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [error, setError] = useState(null);
  // Iter 39 Slice 1b — operator toggle to hide Market Intelligence /
  // Generic Noise rows entirely. Default: show everything (qualified
  // targets float to the top via the backend sort), but Atech can
  // hide market intel when working through high-priority targets.
  const [qualifiedOnly, setQualifiedOnly] = useState(false);

  // Load sectors on mount.
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/tracker-tech/sectors`);
        if (!r.ok) throw new Error('Failed to load technology sectors');
        const body = await r.json();
        setSectors(body.sectors || []);
      } catch (e) {
        setError(messageFor(e, { context: 'tracker-tech' }));
      }
    })();
  }, []);

  // When sector changes, fetch products.
  useEffect(() => {
    if (!sector) { setProducts([]); setProduct(''); return; }
    (async () => {
      try {
        const r = await fetch(
          `${API_URL}/api/tracker-tech/products?sector=${encodeURIComponent(sector)}`,
        );
        if (!r.ok) throw new Error('Failed to load products');
        const body = await r.json();
        setProducts(body.products || []);
        setProduct('');
      } catch (e) {
        setError(messageFor(e, { context: 'tracker-tech' }));
      }
    })();
  }, [sector]);

  // When product changes, fetch vectors.
  useEffect(() => {
    if (!product) { setVectors([]); setVector(''); setVectorsLoading(false); return; }
    (async () => {
      setVectorsLoading(true);
      try {
        const r = await fetch(
          `${API_URL}/api/tracker-tech/vectors?product=${encodeURIComponent(product)}`,
        );
        if (!r.ok) throw new Error('Failed to load vectors');
        const body = await r.json();
        setVectors(body.vectors || []);
        setVector('');
      } catch (e) {
        setError(messageFor(e, { context: 'tracker-tech' }));
      } finally {
        setVectorsLoading(false);
      }
    })();
  }, [product]);

  // Load existing tenant-scoped targets so the table is never empty
  // on first paint when prior runs have already persisted rows.
  useEffect(() => {
    (async () => {
      setLoadingExisting(true);
      try {
        const r = await fetch(`${API_URL}/api/tracker-tech/targets?limit=50`);
        if (r.ok) {
          const body = await r.json();
          setTargets(body.targets || []);
        }
      } catch { /* non-fatal */ }
      finally { setLoadingExisting(false); }
    })();
  }, []);

  const runDiscovery = async () => {
    if (!sector || !product || !vector) {
      setError('Pick a sector, product and vector first.');
      return;
    }
    setError(null); setRunning(true);
    try {
      const r = await fetch(`${API_URL}/api/tracker-tech/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sector, product, vector, max_targets: 25 }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        const detail = body.detail;
        const msg = typeof detail === 'string' ? detail :
          (detail?.message || 'Discovery run failed.');
        throw new Error(msg);
      }
      const body = await r.json();
      setTargets(body.targets || []);
    } catch (e) {
      setError(messageFor(e, { context: 'tracker-tech' }));
    } finally {
      setRunning(false);
    }
  };

  const dismiss = async (id) => {
    try {
      const r = await fetch(`${API_URL}/api/tracker-tech/targets/${id}/dismiss`, {
        method: 'POST',
      });
      if (r.ok) setTargets((rows) => rows.filter((t) => t.id !== id));
    } catch { /* non-fatal */ }
  };

  // Iter 39 Slice 1d — re-classify existing rows with the new
  // deterministic + LLM pipeline. Used as a one-shot upgrade for
  // rows persisted before the slice-1d taxonomy existed.
  const [backfilling, setBackfilling] = useState(false);
  const runBackfill = async () => {
    setError(null); setBackfilling(true);
    try {
      const r = await fetch(`${API_URL}/api/tracker-tech/backfill?apply_llm=true`, {
        method: 'POST',
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail?.message || 'Backfill failed.');
      }
      // Refresh the table so the new fields show.
      const refresh = await fetch(`${API_URL}/api/tracker-tech/targets?limit=50`);
      if (refresh.ok) {
        const body = await refresh.json();
        setTargets(body.targets || []);
      }
    } catch (e) {
      setError(messageFor(e, { context: 'tracker-tech' }));
    } finally {
      setBackfilling(false);
    }
  };

  return (
    <div className="tracker-technology-dashboard" data-testid="tracker-technology-dashboard"
         style={{ padding: 16 }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 20, marginBottom: 4 }}>Technology Sector Tracker</h2>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 0 }}>
          Discover companies — across any industry — showing intent around a
          technology, platform or vendor. Slice 1 ships VMware Displacement
          for the Atech use case; other products will follow.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <label style={{ minWidth: 240 }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 4 }}>
            Technology Sector
          </div>
          <select
            data-testid="tt-sector-select"
            value={sector}
            onChange={(e) => setSector(e.target.value)}
            style={{ width: '100%', padding: '8px 10px' }}
          >
            <option value="">Select a sector…</option>
            {sectors.map((s) => (
              <option key={s.name} value={s.name}>
                {s.name} {s.product_count === 0 ? '(no products yet)' : `(${s.product_count})`}
              </option>
            ))}
          </select>
        </label>

        <label style={{ minWidth: 240 }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 4 }}>
            Product
          </div>
          <select
            data-testid="tt-product-select"
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            disabled={!sector}
            style={{ width: '100%', padding: '8px 10px' }}
          >
            <option value="">Select a product…</option>
            {products.map((p) => (
              <option key={p.name} value={p.name}>
                {p.name} {p.vector_count === 0 ? '(no vectors yet)' : `(${p.vector_count} vector${p.vector_count === 1 ? '' : 's'})`}
              </option>
            ))}
          </select>
        </label>

        <label style={{ minWidth: 320 }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 4 }}>
            Vector
          </div>
          <select
            data-testid="tt-vector-select"
            value={vector}
            onChange={(e) => setVector(e.target.value)}
            disabled={!product || vectors.length === 0}
            style={{ width: '100%', padding: '8px 10px' }}
          >
            <option value="">
              {product && vectors.length === 0 ? 'No vectors configured yet' : 'Select a vector…'}
            </option>
            {vectors.map((v) => (
              <option key={v.name} value={v.name}>{v.name}</option>
            ))}
          </select>
          {product && !vectorsLoading && vectors.length === 0 && (
            <div
              data-testid="tt-no-vectors-affordance"
              role="status"
              style={{
                marginTop: 6, padding: '8px 10px',
                fontSize: 11, lineHeight: 1.4,
                color: 'var(--text-secondary)',
                background: 'rgba(78,161,255,0.06)',
                borderLeft: '2px solid rgba(78,161,255,0.5)',
                borderRadius: 4,
              }}
            >
              <strong style={{ color: 'var(--text-primary, #e6ecf3)', fontWeight: 600 }}>
                No vectors configured yet
              </strong>{' '}
              — pick another product or request this vector.
              <div style={{ marginTop: 3, opacity: 0.8 }}>
                This product is in the catalogue, but discovery logic
                has not been enabled yet.
              </div>
            </div>
          )}
        </label>

        <button
          type="button"
          data-testid="tt-run-button"
          onClick={runDiscovery}
          disabled={running || !sector || !product || !vector}
          className="btn btn-primary"
          style={{ alignSelf: 'flex-end', padding: '8px 16px' }}
        >
          {running ? 'Running…' : 'Run discovery'}
        </button>

        <label
          data-testid="tt-qualified-only-toggle-label"
          style={{ alignSelf: 'flex-end', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)' }}
        >
          <input
            type="checkbox"
            data-testid="tt-qualified-only-toggle"
            checked={qualifiedOnly}
            onChange={(e) => setQualifiedOnly(e.target.checked)}
          />
          Hide market intelligence
        </label>

        <button
          type="button"
          data-testid="tt-backfill-button"
          onClick={runBackfill}
          disabled={backfilling || targets.length === 0}
          title="Re-classify existing rows with the slice-1d pipeline (deterministic + LLM)."
          className="btn-link"
          style={{
            alignSelf: 'flex-end', padding: '6px 10px', fontSize: 11,
            background: 'transparent',
            border: '1px solid var(--border-color, #1f2630)',
            borderRadius: 6,
            color: 'var(--text-secondary)',
            cursor: backfilling ? 'progress' : 'pointer',
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}
        >
          {backfilling ? 'Re-qualifying…' : 'Re-qualify with AI'}
        </button>
      </div>

      {error && (
        <div className="auth-error" data-testid="tt-error" style={{ marginBottom: 12 }}>
          {error}
        </div>
      )}

      {targets.length > 0 && (() => {
        const counts = summariseVerdicts(targets);
        const pill = (label, n, kind, color) => (
          <span
            key={kind}
            data-testid={`tt-summary-${kind}`}
            style={{
              fontSize: 11,
              color: n > 0 ? (color || 'var(--text-primary, #e6ecf3)') : 'var(--text-secondary)',
              opacity: n > 0 ? 1 : 0.6,
              fontWeight: n > 0 ? 600 : 400,
            }}
          >
            <strong style={{ fontWeight: 700 }}>{n}</strong>{' '}
            <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>{label}</span>
          </span>
        );
        const sep = (i) => (
          <span key={`s${i}`} aria-hidden="true" style={{ color: 'var(--text-secondary)', opacity: 0.4 }}>·</span>
        );
        return (
          <div
            data-testid="tt-discovery-summary"
            style={{
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              marginBottom: 10, padding: '6px 10px',
              fontSize: 11, letterSpacing: '0.01em',
              color: 'var(--text-secondary)',
              borderLeft: '2px solid var(--border-color, #1f2630)',
            }}
          >
            <span style={{ textTransform: 'uppercase', letterSpacing: '0.08em', opacity: 0.7 }}>
              Discovery quality
            </span>
            {pill('direct opportunities', counts.direct, 'direct', '#3bb771')}
            {sep(1)}
            {pill('monitor', counts.monitor, 'monitor', '#4ea1ff')}
            {sep(2)}
            {pill('partner signals', counts.partner, 'partner', '#9775fa')}
            {sep(3)}
            {pill('market intel', counts.marketIntel, 'market-intel')}
            {sep(4)}
            {pill('noise', counts.noise, 'noise')}
          </div>
        );
      })()}

      <WideTableScroll>
        <table
          className="tracker-table tracker-table--tight"
          data-testid="tt-results-table"
          style={{ minWidth: 1400, width: '100%' }}
        >
          <thead>
            <tr>
              <th style={{ minWidth: 84 }}>Priority</th>
              <th style={{ minWidth: 220 }}>Company / Organisation</th>
              <th style={{ minWidth: 160 }}>Verdict</th>
              <th style={{ minWidth: 130 }}>Type</th>
              <th style={{ minWidth: 170 }}>Role</th>
              <th style={{ minWidth: 200 }}>Signal Type</th>
              <th style={{ minWidth: 260 }}>Why This Company</th>
              <th style={{ minWidth: 260 }}>Evidence</th>
              <th style={{ minWidth: 220 }}>Suggested Angle</th>
              <th style={{ minWidth: 72 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {targets.length === 0 && !loadingExisting && (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', padding: 24, color: 'var(--text-secondary)' }}>
                  No technology targets yet — pick a sector/product/vector and
                  click <strong>Run discovery</strong>.
                </td>
              </tr>
            )}
            {targets
              .filter((t) => !qualifiedOnly || (
                t.opportunity_verdict
                  ? !['market_intelligence', 'noise'].includes(t.opportunity_verdict)
                  : t.qualified_target !== false
              ))
              .map((t) => {
              const chip = signalLevelChip(t.signal_level);
              const bucket = priorityBucket(t.priority);
              const rtype = resultTypeChip(t.result_type);
              const verdict = verdictChip(t.opportunity_verdict);
              const rowStyle = t.opportunity_verdict
                ? rowStyleForVerdict(t.opportunity_verdict)
                : rowStyleForQualification(t);
              const org = organisationCell(t);
              const orgConfChip = confidenceChip(org.confidence);
              const orgSrc = organisationSourceLabel(org.source);
              return (
                <tr key={t.id || t.url} data-testid={`tt-row-${t.id || t.url}`} style={rowStyle}>
                  <td style={{ verticalAlign: 'top', padding: '8px 10px' }}>
                    <span
                      data-testid="tt-priority-chip"
                      style={{
                        display: 'inline-block', padding: '3px 8px', borderRadius: 6,
                        background: bucket.bg, color: bucket.fg, fontWeight: 700, fontSize: 12,
                      }}
                    >
                      {t.priority}
                    </span>
                    <div style={{ fontSize: 10, color: chip.color, marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {chip.label}
                    </div>
                  </td>
                  <td data-testid="tt-organisation" style={{ verticalAlign: 'top', padding: '8px 10px' }}>
                    <div
                      data-testid="tt-organisation-name"
                      style={{
                        fontWeight: org.isUnknown ? 500 : 700,
                        fontSize: 13,
                        color: org.isUnknown ? 'var(--text-secondary)' : 'var(--text-primary, #e6ecf3)',
                        fontStyle: org.isUnknown ? 'italic' : 'normal',
                        lineHeight: 1.25,
                      }}
                    >
                      {org.name}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                      <span
                        data-testid="tt-organisation-confidence"
                        title={`Confidence: ${org.confidence}`}
                        style={{
                          display: 'inline-block', padding: '1px 7px', borderRadius: 999,
                          background: orgConfChip.bg, color: orgConfChip.color,
                          fontSize: 10, fontWeight: 600, letterSpacing: '0.02em',
                        }}
                      >
                        {orgConfChip.label}
                      </span>
                      {orgSrc && (
                        <span
                          data-testid="tt-organisation-source"
                          style={{ fontSize: 10, color: 'var(--text-secondary)' }}
                        >
                          {orgSrc}
                        </span>
                      )}
                      {t.llm_qualified && (
                        <span
                          data-testid="tt-llm-qualified"
                          title="Qualified by LLM"
                          style={{
                            fontSize: 9, padding: '1px 5px', borderRadius: 4,
                            background: 'rgba(78,161,255,0.16)', color: '#4ea1ff',
                            fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em',
                          }}
                        >
                          AI
                        </span>
                      )}
                    </div>
                  </td>
                  <td data-testid="tt-verdict" style={{ verticalAlign: 'top', padding: '8px 10px' }}>
                    <span
                      data-testid="tt-verdict-chip"
                      style={{
                        display: 'inline-block', padding: '3px 10px', borderRadius: 999,
                        background: verdict.bg, color: verdict.color,
                        fontSize: 11, fontWeight: verdict.strong ? 700 : 600,
                      }}
                    >
                      {verdict.label}
                    </span>
                  </td>
                  <td style={{ verticalAlign: 'top', padding: '8px 10px' }}>
                    <span
                      data-testid="tt-result-type-chip"
                      style={{
                        display: 'inline-block', padding: '2px 9px', borderRadius: 999,
                        background: rtype.bg, color: rtype.color,
                        fontSize: 11, fontWeight: 600,
                      }}
                    >
                      {rtype.label}
                    </span>
                  </td>
                  <td style={{ verticalAlign: 'top', padding: '8px 10px' }}>
                    <span style={rolePillStyle(t.role)} data-testid="tt-role-chip">{t.role}</span>
                  </td>
                  <td data-testid="tt-signal-type" style={{ verticalAlign: 'top', padding: '8px 10px', fontSize: 13 }}>
                    {t.signal_type}
                  </td>
                  <td data-testid="tt-why-this-company" style={{ verticalAlign: 'top', padding: '8px 10px', fontSize: 13, lineHeight: 1.4 }}>
                    {t.why_this_company || t.why_now}
                  </td>
                  <td data-testid="tt-evidence" style={{ verticalAlign: 'top', padding: '8px 10px' }}>
                    <a
                      href={t.url}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: 'var(--text-secondary)', fontSize: 12, textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                    >
                      {t.title || t.url}
                    </a>
                    {t.snippet && (
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3, opacity: 0.75, lineHeight: 1.35 }}>
                        {t.snippet.length > 160 ? `${t.snippet.slice(0, 160)}…` : t.snippet}
                      </div>
                    )}
                  </td>
                  <td data-testid="tt-suggested-angle" style={{ verticalAlign: 'top', padding: '8px 10px', fontSize: 13, lineHeight: 1.4 }}>
                    {t.suggested_angle}
                  </td>
                  <td style={{ verticalAlign: 'top', padding: '8px 10px' }}>
                    <button
                      type="button"
                      className="btn-link"
                      onClick={() => dismiss(t.id)}
                      data-testid="tt-dismiss"
                      style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12 }}
                    >
                      Dismiss
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </WideTableScroll>
    </div>
  );
}
